import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Select, Input, InputGroup, InputLeftElement,
  Button, IconButton, useToast, Spinner, Flex, Divider, Badge, Card, CardBody,
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { apiService } from '../services/apiService';

const ANCIEN_TESTAMENT_COUNT = 39;

export default function BibleInteractive() {
  const toast = useToast();
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);

  const [selectedBook, setSelectedBook] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [chapterData, setChapterData] = useState(null);
  const [loadingChapter, setLoadingChapter] = useState(false);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const fetchBooks = useCallback(async () => {
    try {
      setLoadingBooks(true);
      const data = await apiService.makeRequest('/bible/books');
      setBooks(data.books || []);
      if (data.books && data.books.length > 0) {
        setSelectedBook(data.books.find(b => b.nom === 'Jean')?.nom || data.books[0].nom);
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoadingBooks(false);
    }
  }, [toast]);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);

  const fetchChapter = useCallback(async (book, chapter) => {
    if (!book) return;
    try {
      setLoadingChapter(true);
      const data = await apiService.makeRequest(`/bible/books/${encodeURIComponent(book)}/${chapter}`);
      setChapterData(data);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoadingChapter(false);
    }
  }, [toast]);

  useEffect(() => {
    if (selectedBook) fetchChapter(selectedBook, selectedChapter);
  }, [selectedBook, selectedChapter, fetchChapter]);

  const handleBookChange = (nom) => {
    setSelectedBook(nom);
    setSelectedChapter(1);
    setSearchResults(null);
  };

  const currentBookMeta = books.find(b => b.nom === selectedBook);
  const nbChapitres = chapterData?.nb_chapitres || currentBookMeta?.nb_chapitres || 1;

  const goToChapter = (delta) => {
    const next = selectedChapter + delta;
    if (next < 1 || next > nbChapitres) return;
    setSelectedChapter(next);
  };

  const handleSearch = async () => {
    if (query.trim().length < 3) {
      toast({ title: 'Recherche trop courte', description: '3 caractères minimum', status: 'warning', duration: 3000 });
      return;
    }
    try {
      setSearching(true);
      const data = await apiService.makeRequest(`/bible/search?q=${encodeURIComponent(query.trim())}`);
      setSearchResults(data.results || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSearching(false);
    }
  };

  const goToResult = (r) => {
    setSearchResults(null);
    setQuery('');
    setSelectedBook(r.livre);
    setSelectedChapter(r.chapitre);
  };

  return (
    <Box maxW="900px" mx="auto">
      <Heading size="lg" mb={1}>📖 Bible interactive</Heading>
      <Text color="gray.500" mb={6}>Louis Segond 1910 — parcourez ou recherchez un passage.</Text>

      {/* Recherche */}
      <HStack mb={6}>
        <InputGroup>
          <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
          <Input
            placeholder="Rechercher un mot ou une phrase (ex: amour, grâce...)"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            bg="white"
          />
        </InputGroup>
        <Button colorScheme="green" onClick={handleSearch} isLoading={searching}>Rechercher</Button>
        {searchResults && (
          <Button variant="ghost" onClick={() => { setSearchResults(null); setQuery(''); }}>Fermer</Button>
        )}
      </HStack>

      {searchResults ? (
        <Box>
          <Badge colorScheme="gray" mb={3}>{searchResults.length} résultat(s){searchResults.length >= 100 ? ' (limité à 100)' : ''}</Badge>
          {searchResults.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>Aucun résultat trouvé.</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {searchResults.map((r, i) => (
                <Card key={i} variant="outline" cursor="pointer" _hover={{ borderColor: 'green.400', shadow: 'sm' }}
                  onClick={() => goToResult(r)}>
                  <CardBody py={3}>
                    <Text fontWeight="bold" fontSize="sm" color="green.700" mb={1}>
                      {r.livre} {r.chapitre}:{r.verset}
                    </Text>
                    <Text fontSize="sm" color="gray.700">{r.texte}</Text>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}
        </Box>
      ) : (
        <>
          {/* Sélecteurs livre / chapitre */}
          <Card mb={4}>
            <CardBody>
              <HStack spacing={3} flexWrap="wrap">
                {loadingBooks ? (
                  <Spinner size="sm" />
                ) : (
                  <Select value={selectedBook} onChange={e => handleBookChange(e.target.value)} maxW="260px" bg="white">
                    <optgroup label="Ancien Testament">
                      {books.slice(0, ANCIEN_TESTAMENT_COUNT).map(b => (
                        <option key={b.nom} value={b.nom}>{b.nom}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Nouveau Testament">
                      {books.slice(ANCIEN_TESTAMENT_COUNT).map(b => (
                        <option key={b.nom} value={b.nom}>{b.nom}</option>
                      ))}
                    </optgroup>
                  </Select>
                )}

                <HStack>
                  <IconButton icon={<ChevronLeftIcon />} size="sm" aria-label="Chapitre précédent"
                    onClick={() => goToChapter(-1)} isDisabled={selectedChapter <= 1} />
                  <Select value={selectedChapter} onChange={e => setSelectedChapter(parseInt(e.target.value, 10))} maxW="110px" bg="white">
                    {Array.from({ length: nbChapitres }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>Chapitre {n}</option>
                    ))}
                  </Select>
                  <IconButton icon={<ChevronRightIcon />} size="sm" aria-label="Chapitre suivant"
                    onClick={() => goToChapter(1)} isDisabled={selectedChapter >= nbChapitres} />
                </HStack>
              </HStack>
            </CardBody>
          </Card>

          {/* Texte du chapitre */}
          {loadingChapter ? (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          ) : chapterData ? (
            <Card>
              <CardBody>
                <Heading size="md" mb={4} color="green.800">{chapterData.livre} {chapterData.chapitre}</Heading>
                <VStack align="stretch" spacing={3}>
                  {chapterData.versets.map(v => (
                    <Text key={v.verset} fontSize="md" lineHeight="1.7">
                      <Text as="sup" fontWeight="bold" color="green.600" mr={1}>{v.verset}</Text>
                      {v.texte}
                    </Text>
                  ))}
                </VStack>
                <Divider my={5} />
                <Flex justify="space-between">
                  <Button size="sm" variant="outline" onClick={() => goToChapter(-1)} isDisabled={selectedChapter <= 1}>
                    ← Chapitre précédent
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => goToChapter(1)} isDisabled={selectedChapter >= nbChapitres}>
                    Chapitre suivant →
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          ) : null}
        </>
      )}
    </Box>
  );
}
